import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useUpdateLeadList } from "@/hooks/use-lead-list-actions";
import { usePoolLeads, usePoolOverview } from "@/hooks/use-lead-pools";
import { formatDateShort } from "@/lib/format";
import { LEAD_OUTCOME_LABELS } from "@/lib/lead-stats-labels";
import { POOL_STATE_LABELS, poolStatusText, type PoolState } from "@/lib/lead-pool";
import { cn } from "@/lib/utils";
import LeadAdminActions from "./LeadAdminActions";

const STATES: PoolState[] = ["free", "queued", "resting", "meeting", "rejected"];

interface Props {
  listId: string;
  onBack: () => void;
  onOpenLead: (leadId: string) => void;
}

/** Leads i en pool for admin (VIB-3050 skjerm 5): søk, filter, statustekst og flervalg. */
export default function PoolLeadsView({ listId, onBack, onOpenLead }: Props) {
  const [search, setSearch] = useState("");
  const [state, setState] = useState<PoolState | null>(null);
  const [segment, setSegment] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const { data: lists = [] } = useLeadLists();
  const { data: overview = [] } = usePoolOverview();
  const { data: rows = [], isLoading } = usePoolLeads(listId, search, state);
  const update = useUpdateLeadList();
  const list = lists.find((l) => l.id === listId);
  const counts = overview.find((c) => c.list_id === listId);
  const segments = useMemo(() => [...new Set(rows.map((r) => r.segment).filter((s): s is string => !!s))].sort(), [rows]);
  const visible = segment ? rows.filter((r) => r.segment === segment) : rows;
  const allSelected = visible.length > 0 && visible.every((r) => selected.includes(r.id));

  const saveWater = (field: "low_water" | "high_water", value: string) => {
    const n = value.trim() === "" ? null : Number(value);
    if (n !== null && (!Number.isInteger(n) || n < 0)) return;
    update.mutate({ id: listId, patch: { [field]: n } });
  };

  return (
    <section aria-label={`Leads i ${list?.name ?? "poolen"}`} className="space-y-4">
      <nav className="text-sm">
        <button type="button" className="underline" onClick={onBack}>Pooler</button>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>{list?.name}</span>
      </nav>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-2xl font-semibold">
          {list?.name} <span className="text-muted-foreground">{counts?.total ?? ""}</span>
        </h2>
        {list && (
          <div className="flex items-end gap-2 text-sm">
            <label className="space-y-1">
              <span className="block text-xs text-muted-foreground">Lavvann</span>
              <Input key={`l-${list.low_water}`} type="number" min={0} className="h-8 w-20" defaultValue={list.low_water ?? ""} onBlur={(e) => saveWater("low_water", e.target.value)} />
            </label>
            <label className="space-y-1">
              <span className="block text-xs text-muted-foreground">Høyvann</span>
              <Input key={`h-${list.high_water}`} type="number" min={1} className="h-8 w-20" defaultValue={list.high_water ?? ""} onBlur={(e) => saveWater("high_water", e.target.value)} />
            </label>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk på selskap eller orgnr" aria-label="Søk i poolen" className="h-9 w-64" />
        <div role="group" aria-label="Status" className="flex flex-wrap gap-1">
          <Button size="sm" variant={state === null ? "default" : "outline"} aria-pressed={state === null} onClick={() => setState(null)}>Alle</Button>
          {STATES.map((s) => (
            <Button key={s} size="sm" variant={state === s ? "default" : "outline"} aria-pressed={state === s} onClick={() => setState(s)}>
              {POOL_STATE_LABELS[s]} {counts ? counts[s] : ""}
            </Button>
          ))}
        </div>
        {segments.length > 0 && (
          <select aria-label="Segment" className="h-9 border border-input bg-background px-2 text-sm" value={segment ?? ""} onChange={(e) => setSegment(e.target.value || null)}>
            <option value="">Alle segmenter</option>
            {segments.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      <div className={cn("flex flex-wrap items-center gap-3 border border-dashed border-foreground/40 p-2", selected.length === 0 && "opacity-60")}>
        <span className="text-sm font-medium">{selected.length} valgt</span>
        <LeadAdminActions leadIds={selected} currentListId={listId} onDone={() => setSelected([])} />
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : visible.length === 0 ? (
        <p className="border border-border p-6 text-sm text-muted-foreground">Ingen leads her.</p>
      ) : (
        <div className="overflow-x-auto border-[1.5px] border-foreground">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="w-8 p-2">
                  <Checkbox aria-label="Velg alle" checked={allSelected} onCheckedChange={(v) => setSelected(v === true ? visible.map((r) => r.id) : [])} />
                </th>
                <th className="p-2">Selskap</th>
                <th className="p-2">Sted</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Forsøk</th>
                <th className="p-2">Siste</th>
                <th className="p-2">Prioritet</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="p-2">
                    <Checkbox
                      aria-label={`Velg ${r.name}`}
                      checked={selected.includes(r.id)}
                      onCheckedChange={(v) => setSelected((s) => (v === true ? [...s, r.id] : s.filter((id) => id !== r.id)))}
                    />
                  </td>
                  <td className="p-2">
                    <button type="button" className="font-medium underline-offset-2 hover:underline" onClick={() => onOpenLead(r.id)}>{r.name}</button>
                    <span className="block text-xs text-muted-foreground">{[r.organization_number, r.industry_description].filter(Boolean).join(" · ")}</span>
                  </td>
                  <td className="p-2">{r.municipality}</td>
                  <td className="p-2">{poolStatusText(r)}</td>
                  <td className="p-2 text-right tabular-nums">{r.attempts_count}</td>
                  <td className="p-2 text-xs">
                    {r.last_outcome ? `${LEAD_OUTCOME_LABELS[r.last_outcome] ?? r.last_outcome}${r.last_attempt_at ? ` · ${formatDateShort(r.last_attempt_at)}` : ""}` : "Ikke ringt"}
                  </td>
                  <td className="p-2">{r.priority ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 1000 && <p className="p-2 text-xs text-muted-foreground">Viser de første 1000. Bruk søk eller filter for å finne resten.</p>}
        </div>
      )}
    </section>
  );
}
